import * as THREE from "three";

/**
 * Kiểu tọa độ 3D rút gọn.
 */
export type Vector3Like = Readonly<{
  x: number;
  y: number;
  z: number;
}>;

/**
 * Thông tin bounding box, kích thước và tâm đáy của mô hình 3D.
 */
export type ModelBounds = Readonly<{
  min: Vector3Like;
  max: Vector3Like;
  size: Vector3Like;
  center: Vector3Like;
  /** Tâm đáy của mô hình: x = center.x, y = min.y, z = center.z */
  bottomCenter: Vector3Like;
}>;

/**
 * Các chế độ căn chỉnh tỷ lệ uniform scale theo kích thước thực tế.
 */
export type ScaleFitMode = "width" | "length" | "contain" | "cover";

/**
 * Đầu vào tính toán tỷ lệ scale cho mô hình.
 */
export type ScaleCalculationInput = Readonly<{
  modelSize: Vector3Like;
  targetWidthMeters?: number | undefined;
  targetLengthMeters?: number | undefined;
  metersPerSceneUnit?: number | undefined;
  fitMode?: ScaleFitMode | undefined;
}>;

/**
 * Tuỳ chọn tính toán góc xoay quanh trục đứng (Y).
 */
export type HeadingRotationOptions = Readonly<{
  /**
   * Góc lệch mặt tiền/hướng cổng mặc định của file GLB so với trục Bắc (-Z), tính theo độ.
   * Ví dụ: Nếu cổng model quay về hướng Nam (+Z), offset là 180 độ.
   */
  modelFrontOffsetDeg?: number | undefined;
  /**
   * Đảo chiều góc xoay nếu dùng trong hệ tọa độ ngược chiều chuẩn Three.js.
   */
  invert?: boolean | undefined;
}>;

/**
 * Kết quả sau khi chuẩn hóa pivot và căn tâm đáy mô hình.
 */
export type NormalizedModelResult = Readonly<{
  wrapper: THREE.Group;
  bounds: ModelBounds;
  pivotOffset: THREE.Vector3;
}>;

/**
 * Cấu hình đầy đủ để định vị và biến đổi mô hình GLB trong Three.js scene.
 */
export type GeoreferenceModelConfig = Readonly<{
  model: THREE.Object3D;
  bearingDeg?: number | undefined;
  scale?: number | undefined;
  targetDimensions?:
    | Readonly<{
        widthMeters?: number | undefined;
        lengthMeters?: number | undefined;
      }>
    | undefined;
  metersPerSceneUnit?: number | undefined;
  fitMode?: ScaleFitMode | undefined;
  opacity?: number | undefined;
  position?: Vector3Like | undefined;
  modelFrontOffsetDeg?: number | undefined;
}>;

/**
 * Kết quả sau khi định vị mô hình.
 */
export type GeoreferenceModelResult = Readonly<{
  wrapper: THREE.Group;
  bounds: ModelBounds;
  appliedScale: number;
  appliedRotationY: number;
  appliedOpacity: number;
}>;

/**
 * Đo đạc chính xác bounding box, kích thước và vị trí tâm đáy của mô hình 3D.
 *
 * @param object Mô hình Three.js (Mesh, Group, Scene)
 * @returns Thông tin bounds chi tiết
 */
export function computeModelBounds(object: THREE.Object3D): ModelBounds {
  object.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(object);

  if (box.isEmpty()) {
    return {
      min: { x: 0, y: 0, z: 0 },
      max: { x: 0, y: 0, z: 0 },
      size: { x: 0, y: 0, z: 0 },
      center: { x: 0, y: 0, z: 0 },
      bottomCenter: { x: 0, y: 0, z: 0 },
    };
  }

  const size = new THREE.Vector3();
  const center = new THREE.Vector3();
  box.getSize(size);
  box.getCenter(center);

  return {
    min: { x: box.min.x, y: box.min.y, z: box.min.z },
    max: { x: box.max.x, y: box.max.y, z: box.max.z },
    size: { x: size.x, y: size.y, z: size.z },
    center: { x: center.x, y: center.y, z: center.z },
    bottomCenter: { x: center.x, y: box.min.y, z: center.z },
  };
}

/**
 * Chuẩn hóa pivot của model GLB về tâm đáy (bottom-center: x=center.x, y=min.y, z=center.z).
 *
 * Tạo một `THREE.Group` bọc ngoài, đặt model lệch một vector (-center.x, -min.y, -center.z).
 * Nhờ đó, điểm gốc (0, 0, 0) của Group wrapper trùng khớp chính xác với điểm giữa đáy của mô hình,
 * và mặt đáy của mô hình nằm phẳng trên mặt đất y = 0.
 *
 * @param model Mô hình 3D cần căn pivot
 * @returns Wrapper group chứa model đã được căn pivot, cùng siêu dữ liệu bounds
 */
export function normalizeModelPivot(model: THREE.Object3D): NormalizedModelResult {
  const bounds = computeModelBounds(model);
  const bottomCenter = new THREE.Vector3(
    bounds.bottomCenter.x,
    bounds.bottomCenter.y,
    bounds.bottomCenter.z,
  );

  // Tạo wrapper group
  const wrapper = new THREE.Group();
  wrapper.name = `${model.name || "model"}_pivot_wrapper`;

  // Dịch chuyển model sao cho tâm đáy trùng đúng (0, 0, 0) của wrapper
  // newPosition = currentPosition - currentBottomCenter
  const pivotOffset = new THREE.Vector3().sub(bottomCenter);
  model.position.sub(bottomCenter);
  wrapper.add(model);

  return {
    wrapper,
    bounds,
    pivotOffset,
  };
}

/**
 * Tính toán tỷ lệ scale đồng dạng (uniform scale) từ kích thước gốc của model sang kích thước thực tế.
 *
 * @param input Kích thước model và kích thước thực tế mục tiêu (mét)
 * @returns Hệ số scale dương
 */
export function calculateUniformScale(input: ScaleCalculationInput): number {
  const { modelSize, targetWidthMeters, targetLengthMeters, metersPerSceneUnit, fitMode = "contain" } =
    input;

  // 1. Trường hợp có tỉ lệ quy ước metersPerSceneUnit (1 đơn vị scene = N mét thực địa)
  if (metersPerSceneUnit !== undefined && metersPerSceneUnit > 0) {
    return 1 / metersPerSceneUnit;
  }

  const modelWidth = Math.max(1e-6, modelSize.x);
  const modelLength = Math.max(1e-6, modelSize.z);

  const scaleX =
    targetWidthMeters !== undefined && targetWidthMeters > 0
      ? targetWidthMeters / modelWidth
      : undefined;

  const scaleZ =
    targetLengthMeters !== undefined && targetLengthMeters > 0
      ? targetLengthMeters / modelLength
      : undefined;

  if (scaleX !== undefined && scaleZ !== undefined) {
    switch (fitMode) {
      case "width":
        return scaleX;
      case "length":
        return scaleZ;
      case "cover":
        return Math.max(scaleX, scaleZ);
      case "contain":
      default:
        return Math.min(scaleX, scaleZ);
    }
  }

  if (scaleX !== undefined) return scaleX;
  if (scaleZ !== undefined) return scaleZ;

  return 1.0;
}

/**
 * Chuyển đổi góc phương vị địa lý (bearing, đo theo độ 0..360 theo chiều kim đồng hồ từ Bắc)
 * sang góc quay rotation.y (radian) trong Three.js scene.
 *
 * Quy ước Three.js trong dự án:
 * - Y hướng lên (+Y up), X hướng Đông (+X East), Z hướng Nam (+Z South), hướng Bắc là -Z.
 * - Góc phương vị bearing tăng theo chiều kim đồng hồ từ Bắc (0°: Bắc, 90°: Đông, 180°: Nam, 270°: Tây).
 * - Phép xoay theo chiều kim đồng hồ quanh trục đứng +Y trong Three.js ứng với góc âm:
 *   rotation.y = - (bearingDeg * Math.PI) / 180.
 *
 * @param bearingDeg Góc phương vị cạnh mặt tiền tính bằng độ (0 - 360)
 * @param options Cấu hình bù góc cổng mặc định của model hoặc đảo chiều
 * @returns Góc rotation.y (radian)
 */
export function calculateHeadingRotation(
  bearingDeg: number,
  options?: HeadingRotationOptions,
): number {
  const offset = options?.modelFrontOffsetDeg ?? 0;
  const rawBearing = (bearingDeg + offset) % 360;
  const normalizedDeg = rawBearing < 0 ? rawBearing + 360 : rawBearing;

  const rad = (normalizedDeg * Math.PI) / 180;
  return options?.invert ? rad : -rad;
}

/**
 * Hàm tiện ích chuyển nhanh bearing sang rotation.y (radian).
 */
export function bearingToRotationY(bearingDeg: number, invert = false): number {
  return calculateHeadingRotation(bearingDeg, { invert });
}

/**
 * Áp dụng độ trong suốt (opacity) cho toàn bộ vật liệu (materials) của mô hình.
 * Hỗ trợ thanh trượt opacity (0.0 -> 1.0) để người dùng đối soát mô hình 3D đè lên bản đồ vệ tinh.
 *
 * @param model Mô hình 3D
 * @param opacity Mức trong suốt trong khoảng [0, 1]
 */
export function applyModelOpacity(model: THREE.Object3D, opacity: number): void {
  const clamped = Math.max(0, Math.min(1, opacity));
  const isTransparent = clamped < 1.0;

  model.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (mesh.isMesh) {
      if (Array.isArray(mesh.material)) {
        mesh.material.forEach((mat) => {
          mat.transparent = isTransparent;
          mat.opacity = clamped;
          mat.depthWrite = !isTransparent;
          mat.needsUpdate = true;
        });
      } else if (mesh.material) {
        mesh.material.transparent = isTransparent;
        mesh.material.opacity = clamped;
        mesh.material.depthWrite = !isTransparent;
        mesh.material.needsUpdate = true;
      }
    }
  });
}

/**
 * Định vị toàn diện mô hình GLB:
 * 1. Chuẩn hóa pivot đưa tâm đáy về (0, 0, 0)
 * 2. Tính và gán uniform scale theo kích thước thực địa (mét) hoặc metersPerSceneUnit
 * 3. Xoay rotation.y quanh trục đứng theo góc phương vị cạnh mặt tiền
 * 4. Gán vị trí tọa độ địa phương (nếu có)
 * 5. Thiết lập opacity cho mô hình
 *
 * @param config Cấu hình định vị
 * @returns Kết quả wrapper group sẵn sàng gắn vào Three.js scene
 */
export function georeferenceGlbModel(config: GeoreferenceModelConfig): GeoreferenceModelResult {
  const { wrapper, bounds } = normalizeModelPivot(config.model);

  const appliedScale =
    config.scale !== undefined && config.scale > 0
      ? config.scale
      : calculateUniformScale({
          modelSize: bounds.size,
          targetWidthMeters: config.targetDimensions?.widthMeters,
          targetLengthMeters: config.targetDimensions?.lengthMeters,
          metersPerSceneUnit: config.metersPerSceneUnit,
          fitMode: config.fitMode,
        });

  wrapper.scale.set(appliedScale, appliedScale, appliedScale);

  const bearing = config.bearingDeg ?? 0;
  const appliedRotationY = calculateHeadingRotation(bearing, {
    modelFrontOffsetDeg: config.modelFrontOffsetDeg,
  });
  wrapper.rotation.set(0, appliedRotationY, 0);

  if (config.position) {
    wrapper.position.set(config.position.x, config.position.y, config.position.z);
  }

  const appliedOpacity = config.opacity !== undefined ? config.opacity : 1.0;
  if (config.opacity !== undefined) {
    applyModelOpacity(wrapper, config.opacity);
  }

  return {
    wrapper,
    bounds,
    appliedScale,
    appliedRotationY,
    appliedOpacity,
  };
}

/**
 * Kiểu tóm tắt kết quả phân tích ranh giới 4 góc (BoundaryAnalysis).
 */
export type BoundaryAnalysisLike = Readonly<{
  outwardNormalBearingDeg: number;
  dimensions: {
    widthM: number;
    depthM: number;
  };
  frontageBearingDeg?: number | undefined;
}>;

/**
 * Định vị mô hình GLB trực tiếp từ kết quả phân tích 4 góc ranh giới.
 *
 * @param model Mô hình 3D
 * @param analysis Kết quả phân tích ranh giới (chứa kích thước và góc bearing pháp tuyến mặt tiền)
 * @param options Tuỳ chọn thêm về opacity, fitMode, modelFrontOffsetDeg
 * @returns Kết quả định vị
 */
export function georeferenceFromBoundaryAnalysis(
  model: THREE.Object3D,
  analysis: BoundaryAnalysisLike,
  options?: Omit<GeoreferenceModelConfig, "model" | "bearingDeg" | "targetDimensions">,
): GeoreferenceModelResult {
  return georeferenceGlbModel({
    model,
    bearingDeg: analysis.outwardNormalBearingDeg,
    targetDimensions: {
      widthMeters: analysis.dimensions.widthM,
      lengthMeters: analysis.dimensions.depthM,
    },
    ...options,
  });
}

/**
 * Tạo khung dây đường ranh giới (LineLoop) hiển thị 4 góc trên mặt đất 3D.
 *
 * @param points Danh sách các điểm 2D cục bộ { x, z } tính bằng mét/scene units
 * @param color Màu đường viền (mặc định xanh neon 0x10b981)
 * @returns Đối tượng LineLoop trong Three.js
 */
export function createBoundaryVisualizer(
  points: readonly Vector3Like[] | readonly { x: number; z: number }[],
  color: number | string = 0x10b981,
): THREE.LineLoop {
  const lineGeometry = new THREE.BufferGeometry();
  const positions: number[] = [];

  for (const pt of points) {
    const y = "y" in pt && typeof pt.y === "number" ? pt.y : 0.05; // Cao hơn mặt đất một chút để tránh z-fighting
    positions.push(pt.x, y, pt.z);
  }

  lineGeometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  const lineMaterial = new THREE.LineBasicMaterial({
    color,
    linewidth: 2,
    depthTest: false,
    transparent: true,
    opacity: 0.85,
  });

  const line = new THREE.LineLoop(lineGeometry, lineMaterial);
  line.name = "boundary_visualizer_line";
  return line;
}

/**
 * Tạo mũi tên chỉ hướng cổng / mặt tiền trong không gian 3D.
 *
 * @param bearingDeg Góc phương vị (độ, 0..360 từ Bắc)
 * @param lengthM Chiều dài mũi tên (mét)
 * @param origin Vị trí đặt gốc mũi tên (mặc định tại gốc 0, 0.1, 0)
 * @param color Màu mũi tên (mặc định đỏ cam 0xef4444)
 * @returns ArrowHelper Three.js
 */
export function createHeadingArrow(
  bearingDeg: number,
  lengthM: number = 15,
  origin: Vector3Like = { x: 0, y: 0.1, z: 0 },
  color: number = 0xef4444,
): THREE.ArrowHelper {
  // Vector hướng tương ứng với bearingDeg (Bắc là -Z, Đông là +X)
  const rad = (bearingDeg * Math.PI) / 180;
  const dir = new THREE.Vector3(Math.sin(rad), 0, -Math.cos(rad)).normalize();
  const originVec = new THREE.Vector3(origin.x, origin.y, origin.z);

  const arrow = new THREE.ArrowHelper(
    dir,
    originVec,
    lengthM,
    color,
    lengthM * 0.25,
    lengthM * 0.15,
  );
  arrow.name = "heading_arrow_helper";
  arrow.userData.bearingDeg = bearingDeg;
  arrow.userData.direction = { x: dir.x, y: dir.y, z: dir.z };
  return arrow;
}

export default {
  computeModelBounds,
  normalizeModelPivot,
  calculateUniformScale,
  calculateHeadingRotation,
  bearingToRotationY,
  applyModelOpacity,
  georeferenceGlbModel,
  georeferenceFromBoundaryAnalysis,
  createBoundaryVisualizer,
  createHeadingArrow,
};
